import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, Building2, BadgeCheck, Users, Plus } from "lucide-react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { useLang, t, fmtMoney } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const KIND_EMOJI = { kitchen: "👨‍🍳", hotel: "🏨", restaurant: "🍽️", store: "🛒" };

const OrgDashboard = ({ orgId, onBack }) => {
  const { user } = useAuth();
  const lang = useLang();
  const [dash, setDash] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [title, setTitle] = useState("staff");
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/org/${orgId}/dashboard`, headers()).then((r) => setDash(r.data)).catch(() => setDash(null));
  };
  useEffect(load, [orgId]);

  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/org/${orgId}/staff`, { email, name, password, title }, headers());
      toast.success(
        data.created_account
          ? `${email} added — account created. Share the temporary password with them.`
          : `${email} added to the team`
      );
      setEmail(""); setName(""); setPassword(""); setTitle("staff");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!dash) return <LoaderCircle className="mx-auto mt-10 h-6 w-6 animate-spin text-[#06B6D4]" />;
  const { org, metrics, staff, recent_orders, is_admin } = dash;
  const loc = user.localization || {};
  const money = (v) => fmtMoney(v, loc.currency || "INR", loc.symbol || "₹");

  return (
    <div className="space-y-6" data-testid="org-dashboard">
      <section className="rounded-3xl border border-[#7C3AED]/30 bg-gradient-to-br from-[#7C3AED]/15 to-[#06B6D4]/10 p-6 backdrop-blur-xl">
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="org-back" onClick={onBack} className="rounded-full border border-white/10 px-3 py-1 font-jbmono text-[10px] text-slate-400 hover:text-white">←</button>
          <span className="text-3xl">{KIND_EMOJI[org.kind] || "🏢"}</span>
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-center gap-2 font-display text-xl font-extrabold" data-testid="org-name">
              {org.name}
              {org.status === "verified" && (
                <span className="flex items-center gap-1 rounded-full bg-[#22C55E]/10 px-3 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider text-[#22C55E]" data-testid="org-verified-badge">
                  <BadgeCheck className="h-3 w-3" /> FUNZ Verified
                </span>
              )}
              {org.status === "pending" && <span className="rounded-full bg-[#F97316]/10 px-3 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider text-[#F97316]" data-testid="org-pending-badge">Verification pending</span>}
              {org.status === "rejected" && <span className="rounded-full bg-[#EF4444]/10 px-3 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider text-[#EF4444]" data-testid="org-rejected-badge">Rejected</span>}
            </p>
            <p className="mt-0.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">
              {org.kind} · {[org.city, org.country].filter(Boolean).join(" · ") || "FUNZ Business"} · {staff.length} team
            </p>
          </div>
          <div className="text-right">
            <p className="font-display text-3xl font-black text-[#22C55E]" data-testid="org-health-score">{metrics.health_score}<span className="text-sm text-slate-500">/100</span></p>
            <p className="font-jbmono text-[9px] uppercase tracking-widest text-slate-500">Business Health</p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="org-metrics">
        {[
          { emoji: "💰", label: "Revenue", value: money(metrics.revenue_total) },
          { emoji: "📦", label: "Orders", value: metrics.orders_total },
          { emoji: "⏳", label: "Pending payments", value: money(metrics.pending_payments) },
          { emoji: "👥", label: "Team", value: metrics.staff_count },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl">
            <span className="text-2xl">{s.emoji}</span>
            <p className="mt-2 font-display text-xl font-extrabold">{s.value}</p>
            <p className="mt-0.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      {recent_orders.length > 0 && (
        <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="org-recent">
          <p className="font-display text-lg font-bold">Latest activity</p>
          <div className="mt-3 space-y-2">
            {recent_orders.map((o, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5">
                <span className="font-jbmono text-[10px] uppercase tracking-wider text-slate-400">{o.status} · {String(o.created_at).slice(0, 10)}</span>
                <span className="font-jbmono text-xs font-bold text-[#22C55E]">{money(o.total)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="org-staff">
        <p className="flex items-center gap-2 font-display text-lg font-bold"><Users className="h-5 w-5 text-[#06B6D4]" /> Team ({staff.length})</p>
        <div className="mt-4 space-y-2.5">
          {staff.map((s) => (
            <div key={s.user_id} data-testid={`org-staff-${s.user_id.slice(-6)}`} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-3.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#7C3AED] to-[#06B6D4] font-display text-xs font-black">
                {(s.name || s.email)[0].toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold">{s.name || s.email}</p>
                <p className="truncate font-jbmono text-[10px] text-slate-500">{s.email}</p>
              </div>
              <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                s.title === "owner" ? "bg-[#F5B300]/10 text-[#F5B300]" : s.title === "manager" ? "bg-[#06B6D4]/10 text-[#06B6D4]" : "bg-white/5 text-slate-400"
              }`}>{s.title}</span>
            </div>
          ))}
        </div>

        {is_admin && (
          <div className="mt-5 border-t border-white/10 pt-5" data-testid="org-invite">
            <p className="mb-1 font-display text-sm font-bold">Add team member</p>
            <p className="mb-3 font-jbmono text-[10px] uppercase tracking-widest text-slate-600">
              FUNZ business emails only — @funz.store / @funz.stores
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              <input data-testid="org-invite-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="chef@funz.store"
                className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
              <input data-testid="org-invite-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (new accounts)"
                className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
              <select data-testid="org-invite-title" value={title} onChange={(e) => setTitle(e.target.value)}
                className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white outline-none focus:border-[#7C3AED]/60">
                <option value="staff">Staff</option>
                <option value="manager">Manager</option>
              </select>
              <input data-testid="org-invite-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Temp password (new accounts)"
                className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
            </div>
            <button data-testid="org-invite-btn" onClick={invite} disabled={busy || !email.trim()}
              className="mt-3 flex items-center gap-2 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
              {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add to team
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export const OrgPanel = () => {
  const [orgs, setOrgs] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/org/mine`, headers()).then((r) => setOrgs(r.data.orgs)).catch(() => setOrgs([]));
  }, []);

  if (active) return <OrgDashboard orgId={active} onBack={() => setActive(null)} />;
  if (orgs === null) return <LoaderCircle className="mx-auto mt-10 h-6 w-6 animate-spin text-[#06B6D4]" />;

  return (
    <div className="space-y-4" data-testid="org-panel">
      <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl">
        <p className="flex items-center gap-2 font-display text-xl font-bold"><Building2 className="h-5 w-5 text-[#A78BFA]" /> My Businesses</p>
        <p className="mt-1 text-xs text-slate-500">Your kitchen, hotel, restaurant or store command centers.</p>
        {orgs.length === 0 ? (
          <p className="mt-5 rounded-2xl border border-dashed border-white/15 p-6 text-center text-sm text-slate-500" data-testid="org-empty">
            No business assigned yet — the FUNZ super admin appoints kitchen, hotel, restaurant and store admins.
          </p>
        ) : (
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {orgs.map((o) => (
              <button key={o.id} data-testid={`org-open-${o.id.slice(-6)}`} onClick={() => setActive(o.id)}
                className="group rounded-2xl border border-white/10 bg-[#09090B]/80 p-5 text-left transition-all hover:-translate-y-1 hover:border-[#7C3AED]/50">
                <span className="inline-block text-3xl transition-transform group-hover:scale-125">{KIND_EMOJI[o.kind] || "🏢"}</span>
                <p className="mt-2.5 text-sm font-bold">{o.name}</p>
                <p className="mt-0.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">
                  {o.kind} · {o.my_title} · {o.status}
                </p>
                <p className="mt-1 font-jbmono text-[10px] text-[#06B6D4]">{t("en", "open_arrow")}</p>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
